import { config } from './config';
import { getUrlParamByKey, getFeatureFlagClient, getDomainConfig, isCSR } from '@raisin/cp-utils';
import { getBankPagePath } from 'utils/getBankPagePath';

/**
 * Replaces placeholders in the provided URL with appropriate values.
 *
 * @param {string} url - The URL containing placeholders.
 * @param {string} offerId - The ID of the offer to replace in the URL.
 * @param {string} regflow - The regflow param to use in the URL.
 * @returns {string} The URL with placeholders replaced by actual values.
 */

const replaceUrlPlaceholders = (url: string, offerId: string, regflow: string | null) =>
  url.replace('%s', offerId).replace('regflow=broker', `regflow=${regflow || 'broker'}`);

/**
 * Creates the Product Card Registration link for selected product.
 *
 * @param {string} locale Current page locale.
 * @param {string} type Registration link type.
 * @param {string} selected offer from the Offers Table.
 * @returns {string} Product registration link.
 */
export const buildRegistrationLink = (
  locale,
  type,
  offer,
  b2cUpdateBankProductLink,
  shouldUseBankSlugFallback,
) => {
  const regflow = getUrlParamByKey('regflow');
  const regflowUrl = regflow ? `&regflow=${regflow}` : '';
  const { brokerRegistrationPath, registrationPath } = getDomainConfig(locale);
  const baseURL = isCSR() ? window.location.origin : '';
  const baseRegistrationPath = isCSR() ? `${baseURL}${registrationPath}` : '';
  // eslint-disable-next-line no-useless-escape
  const brokerRegistrationUrl = isCSR() ? `${baseURL}${brokerRegistrationPath}` : '';

  switch (type) {
    case 'broker_register':
      return replaceUrlPlaceholders(brokerRegistrationUrl, offer.id, regflow);
    case 'product_details':
      if (b2cUpdateBankProductLink) {
        return getBankPagePath(locale)({
          shouldUseBankSlugFallback,
          bankName: offer.partner_bank.name,
          bankSlug: offer.partner_bank.bank_slug,
          productId: offer.id,
        });
      }
      return `${config.url.wpOffers}${offer.id}?${regflowUrl}`;

    case 'bc_broker_registration':
      return brokerRegistrationUrl.replace('%s', offer.id);
    case 'bc_register':
    case 'retail_register':
    default:
      return `${baseRegistrationPath}?product=${offer.id}${regflowUrl}`;
  }
};

/**
 * Creates the Product Card Registration async link for selected product.
 *
 * @param {string} locale Current page locale.
 * @param {string} type Registration link type.
 * @param {string} selected offer from the Offers Table.
 * @returns {string} Product registration link.
 */

export const buildAsyncRegistrationLink = async (locale, type, offer) => {
  const featureFlags = await getFeatureFlagClient();
  const b2cUpdateBankProductLink = featureFlags?.variation?.('b2cUpdateBankProductLink', false);
  const shouldUseBankSlugFallback = featureFlags?.variation?.(
    'b2CShouldUseBankSlugFallback',
    false,
  );
  return buildRegistrationLink(
    locale,
    type,
    offer,
    b2cUpdateBankProductLink,
    shouldUseBankSlugFallback,
  );
};

/**
 * Creates the Opening Order flow link for the selected product.
 *
 * @param {string} locale Current page locale.
 * @param {string} offerID Selected offer's Id from the Offers Table.
 * @returns {string} Deposit opening order flow link.
 */
export const buildOpeningOrderFlowLink = async (locale, offer) => {
  const domainLocaleSuffix = getDomainConfig(locale).domainLocaleSuffix;
  return `/${domainLocaleSuffix}deposits/opening/overview/?productId=${offer.id}`;
};

export const safelyParseJSON = (string: string) => {
  try {
    return JSON.parse(string);
  } catch {
    return undefined;
  }
};
